import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Typography,
  Box,
  Skeleton,
  CircularProgress,
} from '@mui/material'
import { useAtom } from 'jotai'
import { useEnableUseCase } from '../../useCases/hooks/useEnableUseCase'
import { useCasesAtom } from '../../useCases/state/useCasesAtom'
import { useState } from 'react'
import { toast } from '@app/components/ui/Toast/toast'
import { queryClient } from '@app/config/queryClient'

export const SuitabilitySettings = () => {
  const [{ data: useCases, isLoading, isError }] = useAtom(useCasesAtom)
  const { mutate: enableUseCaseMutation, isPending } = useEnableUseCase()
  const [pendingSlug, setPendingSlug] = useState<string | null>(null)

  if (isError) {
    return (
      <Typography color="error" variant="body2">
        Failed to load use cases.
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        border: '0.5px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <List disablePadding>
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <ListItem key={i} divider sx={{ py: 1.5 }}>
                <ListItemText
                  primary={<Skeleton width={200} />}
                  secondary={<Skeleton width={120} />}
                />
                <ListItemSecondaryAction>
                  <Skeleton variant="rounded" width={40} height={22} />
                </ListItemSecondaryAction>
              </ListItem>
            ))
          : useCases?.map((useCase, index) => (
              <ListItem key={useCase.slug} divider={index < useCases.length - 1} sx={{ py: 1.5 }}>
                <ListItemText
                  primary={useCase.name}
                  secondary={useCase.slug}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { fontFamily: 'monospace' },
                  }}
                />
                <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {pendingSlug === useCase.slug && <CircularProgress size={16} />}
                  <Switch
                    edge="end"
                    checked={useCase.enabled}
                    disabled={pendingSlug === useCase.slug || isPending}
                    onChange={() => {
                      setPendingSlug(useCase.slug)
                      enableUseCaseMutation(
                        { slug: useCase.slug, enabled: !useCase.enabled },
                        {
                          onSuccess: () => {
                            toast({
                              title: `  ${useCase.enabled ? 'Disabled' : 'Enabled'} ${useCase.name}`,
                              type: 'success',
                              position: 'top-center',
                            })
                            queryClient.invalidateQueries({ queryKey: ['layer-suitability'] })
                          },

                          onSettled: () => setPendingSlug(null),
                        },
                      )
                    }}
                    inputProps={{ 'aria-label': `Toggle ${useCase.name}` }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
      </List>
    </Box>
  )
}
